import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import HeroSection from '../components/HeroSection';
import LoginModal from '../components/LoginModal';
import ArticleCard from '../components/ArticleCard';
import ProjectCard from '../components/ProjectCard';
import { Article, Project } from '../types';
import { getArticles } from '../api/articles';
import { getProjects } from '../api/projects';
import { useAuth } from '../context/AuthContext';

const AtlasIcon: React.FC<{ type: 'layers' | 'wrench' | 'terminal' }> = ({ type }) => {
  if (type === 'wrench') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.7 6.3a4.2 4.2 0 0 0-5.2 5.2L4 17a2.1 2.1 0 1 0 3 3l5.5-5.5a4.2 4.2 0 0 0 5.2-5.2l-2.8 2.8-2.2-2.2 2.8-2.8a4.2 4.2 0 0 0-.8-.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  }

  if (type === 'terminal') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 5 6 7-6 7M13 19h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  }

  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 8 4.5-8 4.5-8-4.5L12 3Zm-8 9 8 4.5 8-4.5M4 16.5l8 4.5 8-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const ArrowUpRight: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const HomePage: React.FC = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [articlesData, projectsData] = await Promise.all([
          getArticles({}, isAuthenticated ? token : null, false),
          getProjects({}, isAuthenticated ? token : null, false),
        ]);
        setArticles(articlesData);
        setProjects(projectsData);
      } catch (err) {
        console.error('Ошибка при загрузке данных:', err);
        setError('Материалы временно недоступны. Попробуйте обновить страницу позже.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isAuthenticated]);

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
    document.body.style.overflow = '';
  };

  const featuredProject = projects[0];

  return (
    <MainLayout>
      <div className="atlas-home">
        <HeroSection onLoginClick={openLoginModal} />

        <nav className="atlas-topic-nav" aria-label="Разделы портала">
          <a href="#atlas-writing"><AtlasIcon type="layers" /><span>1С и системы</span><span className="atlas-arrow-down">↘</span></a>
          <a href="#atlas-tools"><AtlasIcon type="wrench" /><span>Инструменты</span><span className="atlas-arrow-down">↘</span></a>
          <a href="#atlas-automation"><AtlasIcon type="terminal" /><span>Автоматизация</span><span className="atlas-arrow-down">↘</span></a>
        </nav>

        <div className="atlas-content">
          <section id="atlas-writing" className="atlas-writing" aria-labelledby="atlas-writing-title">
            <div className="atlas-section-heading">
              <h2 id="atlas-writing-title">В базе знаний</h2>
              <span>Статьи и заметки</span>
            </div>

            {loading ? (
              <div className="atlas-loading" aria-live="polite">Загрузка материалов…</div>
            ) : error ? (
              <div className="atlas-notice" role="status">{error}</div>
            ) : articles.length ? (
              <>
                {articles.slice(0, 3).map((article, index) => (
                  <ArticleCard key={article.id} article={article} featured={index === 0} />
                ))}
                {articles.length > 3 && <Link to="/articles" className="atlas-all-link">Все публикации <span aria-hidden="true">→</span></Link>}
              </>
            ) : (
              <div className="atlas-notice">Опубликованных материалов пока нет.</div>
            )}
          </section>

          <aside className="atlas-sidebar">
            <section id="atlas-tools" className="atlas-project-card" aria-labelledby="atlas-project-title">
              <span className="atlas-kicker">ПРОЕКТ</span>
              {featuredProject ? (
                <ProjectCard project={featuredProject} compact />
              ) : (
                <>
                  <h2 id="atlas-project-title">dmishanin.ru 2.0</h2>
                  <p>Перепроектирование<br />персонального сайта.</p>
                  <span className="atlas-status">В разработке</span>
                  <Link to="/projects" className="atlas-card-link">Все проекты <ArrowUpRight /></Link>
                </>
              )}
            </section>

            <section id="atlas-automation" className="atlas-about-card" aria-labelledby="atlas-about-title">
              <span className="atlas-kicker">ОБ АВТОРЕ</span>
              <h2 id="atlas-about-title">Работа<br />и увлечение</h2>
              <p>Создаю программное обеспечение и делюсь опытом.</p>
              <Link to="/#about" className="atlas-card-link">Познакомиться <ArrowUpRight /></Link>
            </section>
          </aside>
        </div>

        <section id="about" className="atlas-about-strip" aria-labelledby="atlas-about-strip-title">
          <div>
            <span className="atlas-kicker">О ПОРТАЛЕ</span>
            <h2 id="atlas-about-strip-title">Практика, которую можно применять.</h2>
          </div>
          <p>Заметки по разработке и DevOps, полезные инструменты и проекты из ежедневной инженерной работы.</p>
        </section>

        <section className="atlas-footer-band" aria-label="Темы портала">
          <span>Разработка</span><span>DevOps</span><span>Инструменты</span><span>Практика</span>
        </section>
      </div>
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
    </MainLayout>
  );
};

export default HomePage;
